// React
import { type ComponentProps } from "react"

// Libraries
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

// Hooks and utilities
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-inter antialiased text-sm font-medium leading-normal whitespace-nowrap rounded-input focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-[color,background-color,border-color,transform] duration-150 select-none motion-safe:active:scale-[0.96] disabled:pointer-events-none",
  {
    variants: {
      size: {
        md: "pl-6 pr-7 py-4 h-auto",
        sm: "px-3 h-8 text-xs",
        lg: "px-8 h-10",
        dialog: "px-8 py-4 h-auto flex-1 min-w-0",
        "dialog-tight": "px-8 py-4 h-auto flex-1 min-w-0 leading-tight",
        "dialog-sm": "px-6 py-3 h-auto flex-1 min-w-0 leading-tight",
        icon: "size-12",
        "icon-sm": "size-8",
        compact: "px-4 py-2 h-auto rounded-xs text-nav leading-[1.5]",
      },
      variant: {
        primary:
          "bg-brand-yellow hover:bg-off-black font-semibold text-off-black hover:text-text-inverse disabled:bg-dinnerplate-white disabled:text-grey",
        destructive:
          "bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-sm",
        "destructive-subtle":
          "bg-chilli-blush font-semibold text-chilli-red hover:bg-chilli-red hover:text-chilli-blush",
        "warning-subtle":
          "bg-custard-yellow font-semibold text-brand-yellow hover:bg-custard-yellow/90",
        "success-subtle":
          "bg-yield-positive-bg font-semibold text-yield-positive hover:bg-yield-positive hover:text-yield-positive-bg",
        "success-solid":
          "bg-yield-positive font-semibold text-yield-positive-bg hover:bg-yield-positive/90",
        outline:
          "bg-background hover:bg-accent border border-input",
        "outline-subtle":
          "bg-transparent border border-dinnerplate-white text-off-black font-semibold hover:bg-dinnerplate-white-light disabled:text-grey-light",
        "outline-light":
          "bg-off-white border border-dinnerplate-white text-off-black font-semibold hover:bg-dinnerplate-white-light disabled:text-grey-light",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "ghost-link":
          "group p-0 font-semibold leading-tight text-grey hover:bg-transparent hover:text-grey",
        "ghost-menu":
          "group p-0 rounded-md hover:bg-dinnerplate-white data-[state=open]:bg-dinnerplate-white",
        tertiary:
          "py-2 pl-4 pr-5 font-semibold text-off-black bg-dinnerplate-white-light hover:bg-dinnerplate-white rounded-xs border border-dinnerplate-white",
        "tertiary-light":
          "py-2 pl-4 pr-5 font-semibold text-off-black bg-off-white hover:bg-dinnerplate-white rounded-xs border border-dinnerplate-white",
        "tertiary-compact":
          "py-1.5 pl-3 pr-3.5 font-semibold text-off-black bg-off-white hover:bg-dinnerplate-white rounded-xs border border-dinnerplate-white",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "primary",
    },
  }
)

export interface ButtonProps
  extends ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = ({ className, variant, size, asChild = false, type, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      type={asChild ? type : (type ?? "button")}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
